import { useTranslation } from "react-i18next";
import { BadgeCheck, Star } from "lucide-react";

const img = (n: string) => `${process.env.PUBLIC_URL}/auth/${n}.webp`;

const SHAPES = [
    { n: "torus-purple", tone: "purple", style: { width: "21vw", left: "35.5%", top: "-13vw" } },
    { n: "blob-cyan", tone: "cyan", style: { width: "14vw", right: "5%", top: "-3.5vw", animationDelay: "-3s" } },
    { n: "wedge-pink", tone: "pink", style: { width: "15vw", right: "-5.5vw", top: "30%", animationDelay: "-6s" } },
    { n: "cylinder-pink", tone: "pink", style: { width: "18vw", right: "-3vw", bottom: "-6vw", animationDelay: "-2s" } },
    { n: "torus-teal", tone: "teal", style: { width: "23vw", left: "31%", bottom: "-12vw", animationDelay: "-5s" } },
] as const;

export function AuthShapes() {
    return (
        <div aria-hidden>
            {SHAPES.map((s) => (
                <img key={s.n} src={img(s.n)} alt="" draggable={false} className={`auth-shape auth-shape--${s.tone} hidden md:block`} style={s.style} />
            ))}
        </div>
    );
}

const AVATARS = [
    { n: "avatar-woman", bg: "#F7A8B8", size: 68, style: { left: 0, top: 12 } },
    { n: "avatar-man", bg: "#A78BFA", size: 58, style: { left: 74, top: -8 } },
    { n: "avatar-boy", bg: "#FACC15", size: 64, style: { left: 50, top: 46 } },
    { n: "avatar-girl", bg: "#5EEAD4", size: 58, style: { left: 110, top: 38 } },
];

function TrustCard() {
    const { t } = useTranslation();
    return (
        <div className="relative inline-block pr-24 sm:pr-28" data-testid="auth-trust-card">
            <div className="auth-trust auth-font flex w-[250px] sm:w-[290px] items-center justify-center rounded-2xl pl-7 pr-12 sm:pr-14 py-10 sm:py-12">
                <p className="text-center text-xl sm:text-2xl font-semibold leading-snug" data-testid="auth-trust-text">“{t("auth.hero.trust")}”</p>
            </div>
            <div className="absolute -right-2 top-2 h-[120px] w-[170px]" aria-hidden>
                {AVATARS.map((a) => (
                    <span key={a.n} className="auth-avatar absolute overflow-hidden rounded-full ring-2 ring-white/70" style={{ ...a.style, width: a.size, height: a.size, background: a.bg }}>
                        <img src={img(a.n)} alt="" className="h-full w-full object-cover object-top scale-110 translate-y-1" draggable={false} />
                    </span>
                ))}
            </div>
            <div className="auth-pill absolute right-0 sm:-right-2 top-[132px] inline-flex items-center gap-1.5 rounded-full px-3 py-1.5" aria-label={t("auth.hero.rating_label")} data-testid="auth-trust-stars">
                <BadgeCheck size={18} fill="#B7791F" stroke="#FDE68A" />
                {[0, 1, 2, 3, 4].map((n) => <Star key={n} size={15} fill="#F5B301" stroke="#F5B301" />)}
            </div>
        </div>
    );
}

export function AuthHero() {
    const { t, i18n } = useTranslation();
    return (
        <div className="relative z-[1]" lang={i18n.language.split("-")[0]} data-testid="auth-hero">
            <h2 className="auth-font auth-headline text-4xl sm:text-5xl xl:text-[3.6rem] font-semibold leading-[1.15] tracking-tight" data-testid="auth-hero-headline">
                {t("auth.hero.headline_1")}<br />{t("auth.hero.headline_2")}
            </h2>
            <p className="sr-only" data-testid="auth-hero-tagline">{t("auth.hero.tagline")}</p>
            <div className="mt-12 lg:mt-16"><TrustCard /></div>
        </div>
    );
}
